// Traduce lo que el usuario quiere guardar a la lista de cambios locales (que luego se
// sincronizan). Funciones puras: no tocan la base, así se testean sin navegador.
import {
  MAX_VARIANTS,
  increasePrice,
  normalizeCode,
  normalizeSearch,
  validateBarcode,
  validateProductName,
  validateSku,
  type ImportPlan,
  type RoundingMode,
  type UnitType,
} from '@pos/core';
import type { CategoryRow, LocalChange, ProductRow, SupplierRow, VariantRow } from '@pos/local-db';

export interface VariantDraft {
  id: string;
  attributes: Record<string, string>;
  /** Centavos. */
  price: number;
  /** Centavos; null = sin cargar. */
  cost: number | null;
  barcode: string | null;
  sku: string | null;
  /** Milésimas. */
  min_stock: number | null;
  active: boolean;
}

export interface ProductDraft {
  id: string;
  name: string;
  category_id: string | null;
  supplier_id: string | null;
  unit_type: UnitType;
  tax_bps: number;
  notes: string | null;
  active: boolean;
  image_path: string | null;
  has_variants: boolean;
  variant_attributes: string[];
  /** Sin variantes: exactamente una (la "por defecto"). */
  variants: VariantDraft[];
}

export interface ExistingProduct {
  product: ProductRow;
  variants: VariantRow[];
  /** Costo conocido en este dispositivo, por id de variante. */
  costs: Map<string, number>;
}

type Changes = LocalChange[];

const PRODUCT_FIELDS = [
  'name',
  'category_id',
  'supplier_id',
  'unit_type',
  'tax_bps',
  'notes',
  'active',
  'image_path',
  'has_variants',
  'variant_attributes',
] as const;

const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

function diff<T extends object>(before: T, after: Partial<T>, fields: readonly (keyof T)[]): Partial<T> {
  const changes: Partial<T> = {};
  for (const f of fields) {
    if (f in after && !same(before[f], after[f])) changes[f] = after[f];
  }
  return changes;
}

/**
 * Costo de una variante. Si este dispositivo no conoce el costo actual (p. ej. el dueño
 * entró sin conexión y los costos no están bajados), se envía alta + modificación: el alta
 * no hace nada si ya existe en el servidor y la modificación deja el valor correcto.
 */
export function costChanges(
  variantId: string,
  businessId: string,
  cost: number | null,
  known: number | undefined,
  now: string,
  isNewVariant = false,
): Changes {
  if (cost === null || cost === known) return [];
  const row = { id: variantId, business_id: businessId, cost, created_at: now, updated_at: now };
  // Variante recién creada: no puede tener costo en el servidor, alcanza con el alta.
  if (isNewVariant) return [{ table: 'variant_costs', kind: 'insert', row }];
  if (known !== undefined) return [{ table: 'variant_costs', kind: 'patch', id: variantId, changes: { cost } }];
  return [
    { table: 'variant_costs', kind: 'insert', row },
    { table: 'variant_costs', kind: 'patch', id: variantId, changes: { cost } },
  ];
}

function variantRow(v: VariantDraft, productId: string, businessId: string, isDefault: boolean, sort: number, now: string) {
  return {
    id: v.id,
    business_id: businessId,
    product_id: productId,
    attributes: isDefault ? {} : v.attributes,
    sku: v.sku,
    barcode: v.barcode,
    price: v.price,
    min_stock: v.min_stock,
    is_default: isDefault,
    active: v.active,
    sort,
    deleted_at: null,
    created_at: now,
    updated_at: now,
  };
}

/** Cambios para crear o editar un producto con sus variantes y costos. */
export function buildProductChanges(
  draft: ProductDraft,
  existing: ExistingProduct | null,
  businessId: string,
  now: string,
): Changes {
  const changes: Changes = [];
  const productFields = {
    name: draft.name.trim(),
    category_id: draft.category_id,
    supplier_id: draft.supplier_id,
    unit_type: draft.unit_type,
    tax_bps: draft.tax_bps,
    notes: draft.notes?.trim() || null,
    active: draft.active,
    image_path: draft.image_path,
    has_variants: draft.has_variants,
    variant_attributes: draft.has_variants ? draft.variant_attributes : [],
  };

  if (!existing) {
    changes.push({
      table: 'products',
      kind: 'insert',
      row: { id: draft.id, business_id: businessId, ...productFields, deleted_at: null, created_at: now, updated_at: now },
    });
  } else {
    const productPatch = diff(existing.product, productFields, PRODUCT_FIELDS);
    if (Object.keys(productPatch).length) changes.push({ table: 'products', kind: 'patch', id: draft.id, changes: productPatch });
  }

  const previous = new Map((existing?.variants ?? []).map((v) => [v.id, v]));
  draft.variants.forEach((v, sort) => {
    const isDefault = !draft.has_variants;
    const before = previous.get(v.id);
    if (!before) {
      changes.push({ table: 'product_variants', kind: 'insert', row: variantRow(v, draft.id, businessId, isDefault, sort, now) });
    } else {
      const wanted = variantRow(v, draft.id, businessId, isDefault, sort, now);
      const patch = diff(before, wanted, ['attributes', 'sku', 'barcode', 'price', 'min_stock', 'is_default', 'active', 'sort', 'deleted_at']);
      if (Object.keys(patch).length) changes.push({ table: 'product_variants', kind: 'patch', id: v.id, changes: patch });
    }
    changes.push(...costChanges(v.id, businessId, v.cost, existing?.costs.get(v.id), now, !before));
  });

  // Variantes que ya no están: se archivan (nunca se borran: pueden tener ventas o stock).
  const kept = new Set(draft.variants.map((v) => v.id));
  for (const v of existing?.variants ?? []) {
    if (!kept.has(v.id) && !v.deleted_at) {
      changes.push({ table: 'product_variants', kind: 'patch', id: v.id, changes: { deleted_at: now, active: false } });
    }
  }
  return changes;
}

export interface InitialStockContext {
  businessId: string;
  deviceId: string | null;
  now: string;
  newId: () => string;
}

/**
 * Movimiento de stock inicial de un producto nuevo (al darlo de alta o al importarlo).
 * Cero o vacío no genera movimiento: el producto queda "sin control de stock".
 */
export function initialStockChange(variantId: string, qtyMilli: number | null, ctx: InitialStockContext): Changes {
  if (!qtyMilli) return [];
  return [
    {
      table: 'stock_movements',
      kind: 'insert',
      row: {
        id: ctx.newId(),
        business_id: ctx.businessId,
        variant_id: variantId,
        type: 'initial',
        qty_milli: qtyMilli,
        reason: null,
        ref_type: null,
        ref_id: null,
        occurred_at: ctx.now,
        device_id: ctx.deviceId,
        created_at: ctx.now,
      },
    },
  ];
}

/** Errores de validación del formulario, por campo (`name`, `variants.2.barcode`, …). */
export function validateProductDraft(draft: ProductDraft, otherVariants: VariantRow[]): Record<string, string> {
  const errors: Record<string, string> = {};
  const nameError = validateProductName(draft.name);
  if (nameError) errors.name = nameError;
  if (draft.variants.length === 0) errors.variants = 'Generá al menos una variante.';
  if (draft.variants.length > MAX_VARIANTS) errors.variants = `Un producto puede tener hasta ${MAX_VARIANTS} variantes.`;
  if (draft.has_variants && draft.variant_attributes.length === 0) errors.variants = 'Indicá al menos un atributo (ej. Talle).';

  const others = otherVariants.filter((v) => !v.deleted_at && v.product_id !== draft.id);
  const takenBarcodes = new Map(others.filter((v) => v.barcode).map((v) => [v.barcode!, v]));
  const takenSkus = new Map(others.filter((v) => v.sku).map((v) => [v.sku!, v]));
  const seenBarcode = new Set<string>();
  const seenSku = new Set<string>();

  draft.variants.forEach((v, i) => {
    const key = (f: string) => `variants.${i}.${f}`;
    if (!Number.isSafeInteger(v.price) || v.price < 0) errors[key('price')] = 'Precio inválido.';
    if (v.cost !== null && (!Number.isSafeInteger(v.cost) || v.cost < 0)) errors[key('cost')] = 'Costo inválido.';
    if (v.barcode) {
      const e = validateBarcode(v.barcode);
      if (e) errors[key('barcode')] = e;
      else if (takenBarcodes.has(v.barcode) || seenBarcode.has(v.barcode)) errors[key('barcode')] = 'Ese código de barras ya lo tiene otro producto.';
      seenBarcode.add(v.barcode);
    }
    if (v.sku) {
      const e = validateSku(v.sku);
      if (e) errors[key('sku')] = e;
      else if (takenSkus.has(v.sku) || seenSku.has(v.sku)) errors[key('sku')] = 'Ese código interno ya lo tiene otro producto.';
      seenSku.add(v.sku);
    }
  });
  return errors;
}

/** Aumento (o rebaja) masivo de precios. Solo genera cambios donde el precio cambia. */
export function buildPriceIncreaseChanges(
  variants: VariantRow[],
  percent: number,
  stepCents: number,
  mode: RoundingMode = 'up',
): Changes {
  return variants
    .map((v) => ({ v, price: increasePrice(v.price, percent, stepCents, mode) }))
    .filter(({ v, price }) => price !== v.price)
    .map(({ v, price }) => ({ table: 'product_variants' as const, kind: 'patch' as const, id: v.id, changes: { price } }));
}

/** Archivar una categoría y dejar sus productos sin categoría. */
export function buildCategoryDeleteChanges(categoryId: string, products: ProductRow[], now: string): Changes {
  return [
    { table: 'categories', kind: 'patch', id: categoryId, changes: { deleted_at: now } },
    ...products
      .filter((p) => p.category_id === categoryId)
      .map((p) => ({ table: 'products' as const, kind: 'patch' as const, id: p.id, changes: { category_id: null } })),
  ];
}

export function buildSupplierDeleteChanges(supplierId: string, products: ProductRow[], now: string): Changes {
  return [
    { table: 'suppliers', kind: 'patch', id: supplierId, changes: { deleted_at: now } },
    ...products
      .filter((p) => p.supplier_id === supplierId)
      .map((p) => ({ table: 'products' as const, kind: 'patch' as const, id: p.id, changes: { supplier_id: null } })),
  ];
}

// ---------------------------------------------------------------------------
// Importación
// ---------------------------------------------------------------------------

export interface ImportTarget {
  variant: VariantRow;
  cost: number | undefined;
}

export interface ImportContext {
  businessId: string;
  now: string;
  newId: () => string;
  categories: CategoryRow[];
  suppliers: SupplierRow[];
  /** Si es false, los productos existentes no se modifican. */
  updateExisting: boolean;
  /**
   * Si se indica, los productos NUEVOS con stock en la planilla arrancan con ese stock
   * (movimiento "stock inicial"). En los existentes el stock no se toca: se ajusta en Stock.
   */
  initialStock?: { deviceId: string | null };
}

export interface ImportResult {
  changes: Changes;
  created: number;
  updated: number;
  newCategories: string[];
  newSuppliers: string[];
}

export interface ImportUpdatePreview {
  row: number;
  /** Nombre en la planilla. */
  fileName: string;
  /** Nombre del producto del catálogo que se va a actualizar. */
  catalogName: string;
  matchedBy: 'barcode' | 'sku';
  code: string;
  oldPrice: number;
  newPrice: number;
  /** Los nombres no se parecen: puede ser un código mal cargado. */
  nameMismatch: boolean;
}

/** ¿Los nombres se parecen? Comparte al menos una palabra significativa. */
export function namesLookAlike(a: string, b: string): boolean {
  const words = (s: string) =>
    new Set(
      normalizeSearch(s)
        .split(/[^a-z0-9]+/)
        .filter((w) => w.length >= 3),
    );
  const wa = words(a);
  return [...words(b)].some((w) => wa.has(w));
}

/** Detalle de qué productos existentes se van a actualizar, para mostrarlo antes de importar. */
export function describeImportUpdates(
  plan: ImportPlan<ImportTarget>,
  productName: (productId: string) => string,
): ImportUpdatePreview[] {
  return plan.updates.map(({ existing, row }) => {
    const catalogName = productName(existing.variant.product_id);
    const byBarcode = !!row.barcode && row.barcode === existing.variant.barcode;
    return {
      row: row.row,
      fileName: row.name,
      catalogName,
      matchedBy: byBarcode ? 'barcode' : 'sku',
      code: (byBarcode ? row.barcode : row.sku) ?? '',
      oldPrice: existing.variant.price,
      newPrice: row.price,
      nameMismatch: !namesLookAlike(row.name, catalogName),
    };
  });
}

export function buildImportChanges(plan: ImportPlan<ImportTarget>, ctx: ImportContext): ImportResult {
  const changes: Changes = [];
  const result: ImportResult = { changes, created: 0, updated: 0, newCategories: [], newSuppliers: [] };
  const categoryIds = new Map(ctx.categories.filter((c) => !c.deleted_at).map((c) => [normalizeSearch(c.name), c.id]));
  const supplierIds = new Map(ctx.suppliers.filter((s) => !s.deleted_at).map((s) => [normalizeSearch(s.name), s.id]));

  const lookup = (name: string | null, map: Map<string, string>, table: 'categories' | 'suppliers', list: string[]) => {
    if (!name) return null;
    const key = normalizeSearch(name);
    let id = map.get(key);
    if (!id) {
      id = ctx.newId();
      map.set(key, id);
      list.push(name);
      const base = { id, business_id: ctx.businessId, name, deleted_at: null, created_at: ctx.now, updated_at: ctx.now };
      changes.push({ table, kind: 'insert', row: table === 'categories' ? { ...base, sort: 0 } : base });
    }
    return id;
  };

  for (const row of plan.creates) {
    const productId = ctx.newId();
    const variantId = ctx.newId();
    const draft: ProductDraft = {
      id: productId,
      name: row.name,
      category_id: lookup(row.category, categoryIds, 'categories', result.newCategories),
      supplier_id: lookup(row.supplier, supplierIds, 'suppliers', result.newSuppliers),
      unit_type: row.unit_type,
      tax_bps: row.tax_bps,
      notes: null,
      active: true,
      image_path: null,
      has_variants: false,
      variant_attributes: [],
      variants: [
        {
          id: variantId,
          attributes: {},
          price: row.price,
          cost: row.cost,
          barcode: row.barcode ? normalizeCode(row.barcode) : null,
          sku: row.sku,
          min_stock: row.min_stock_milli,
          active: true,
        },
      ],
    };
    changes.push(...buildProductChanges(draft, null, ctx.businessId, ctx.now));
    if (ctx.initialStock) {
      changes.push(...initialStockChange(variantId, row.stock_milli, { ...ctx, deviceId: ctx.initialStock.deviceId }));
    }
    result.created++;
  }

  if (ctx.updateExisting) {
    for (const { existing, row } of plan.updates) {
      const patch: Record<string, unknown> = {};
      if (row.price !== existing.variant.price) patch.price = row.price;
      if (row.min_stock_milli !== null && row.min_stock_milli !== existing.variant.min_stock) patch.min_stock = row.min_stock_milli;
      const before = changes.length;
      if (Object.keys(patch).length) changes.push({ table: 'product_variants', kind: 'patch', id: existing.variant.id, changes: patch });
      changes.push(...costChanges(existing.variant.id, ctx.businessId, row.cost, existing.cost, ctx.now));
      if (changes.length > before) result.updated++;
    }
  }
  return result;
}
